import { HomeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Radio, Tooltip } from 'antd';
import DownloadIcon from 'assets/svg/DownloadIcon';
import { colorConstants } from 'config/colorConstants';
import { memo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export const WhitespaceAnalysisBreadcrumb = memo(({ title }: { title: string }) => (
    <nav className="flex items-center" aria-label="Breadcrumb">
        <Link to="/" className="flex items-center text-black/50" aria-label="Home">
            <HomeOutlined className="text-xs" />
        </Link>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <span className="text-xs font-medium leading-[22px] text-fontColor">{title}</span>
    </nav>
));

export const WhitespaceAnalysisPageHeader = memo(({
    title,
    onBack,
    tooltipContent,
    onDownloadClick,
}: {
    title: string;
    onBack: () => void;
    tooltipContent: ReactNode;
    onDownloadClick: () => void;
}) => (
    <div className="flex justify-between items-center w-full pb-3">
        <div className="flex items-center gap-2">
            <button
                type="button"
                onClick={onBack}
                className="flex h-8 w-8 items-center justify-center rounded hover:bg-backgroundGreyAlt transition-colors"
                aria-label="Go back"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M10.7071 5.29289C11.0976 5.68342 11.0976 6.31658 10.7071 6.70711L6.41421 11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H6.41421L10.7071 17.2929C11.0976 17.6834 11.0976 18.3166 10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929L9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289Z"
                        fill={colorConstants.headerColor}
                    />
                </svg>
            </button>
            <h1 className="m-0 text-2xl font-semibold leading-8 tracking-[0.5px] text-fontColor">
                {title}
            </h1>
            <Tooltip
                placement="right"
                color={colorConstants.blackTransparent88}
                overlayInnerStyle={{ width: 'auto', maxWidth: '720px' }}
                title={tooltipContent}
            >
                <InfoCircleOutlined className="cursor-pointer opacity-50" />
            </Tooltip>
        </div>
        <Button
            type="default"
            onClick={onDownloadClick}
            className="!h-8 !w-8 !min-w-8 !p-0 !inline-flex !items-center !justify-center !rounded-[6px] !shadow-sm"
            style={{ borderColor: colorConstants.greyAlt }}
            aria-label="Download"
        >
            <DownloadIcon />
        </Button>
    </div>
));

export const WhitespaceAxisRadioGroup = <T extends string>({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: T;
    options: readonly T[];
    onChange: (nextValue: T) => void;
}) => (
    <div className="w-[180px]">
        <p className="mb-2 text-[14px] font-semibold leading-[22px]" style={{ color: colorConstants.axisLabelGrey }}>{label}</p>
        <Radio.Group value={value} onChange={(event) => onChange(event.target.value as T)} className="flex flex-col gap-2">
            {options.map((option) => (
                <Radio key={option} value={option} className="!m-0">
                    <span className="rounded-md px-1 py-0.5" style={{ backgroundColor: value === option ? colorConstants.axisSelectedBg : 'transparent' }}>
                        {option}
                    </span>
                </Radio>
            ))}
        </Radio.Group>
    </div>
);
